import {I18n} from 'aws-amplify/utils';
import {
    Alert,
    Anchor,
    Badge,
    Button,
    Code,
    Group,
    List,
    Stack,
    Text,
} from '@mantine/core';
import {FlexiCopyButton} from '@flexinet/ui-components';

import {CONNECT_PROVIDER_DOCS_URL} from '../config';
import {RoleGrantKey, RoleGrantSelection} from '../services/rolePolicyBuilder';
import {
    actionsAdded,
    elevatedPolicyFor,
    grantDefinition,
} from '../services/grantElevation';
import {productCopy} from '../../../config/productCopy';

/**
 * FNP-1365 — THE ASK, AT THE MOMENT IT IS NEEDED.
 *
 * A customer who withheld "delete hosted zones" has just clicked Delete. This
 * is what they meet instead of a hidden control or a failure at the provider.
 *
 * ⚠️ THIS IS AN INVITATION, NOT AN ERROR, AND EVERY CHOICE HERE FOLLOWS FROM
 * THAT. Blue and not red; "needs one more permission" and not "permission
 * denied"; and nothing about it is styled as a failure, because nothing has
 * failed — the customer made a deliberate choice at connect time and is now
 * meeting its consequence, exactly as the connect drawer told them they would.
 *
 * ⚠️ IT MUST NOT BE A WALL. The ticket is explicit: declining has to leave the
 * customer able to finish the job themselves, and an elevation prompt that
 * blocks the task is worse than the up-front checkbox it replaces. So the
 * do-it-in-AWS path is stated as a real option beside the grant, not buried as
 * a consolation after a refusal.
 *
 * ⚠️ THE POLICY SHOWN IS COMPLETE, AND THE COPY SAYS "REPLACE". An IAM inline
 * policy is replaced by what you paste, never merged, so handing over only the
 * missing action would revoke everything the customer already granted and break
 * the connection they arrived with. `elevatedPolicyFor` returns current-plus-one
 * for that reason — see the note in `grantElevation.ts`.
 *
 * ⚠️ RE-DECLARE, NOT RE-VALIDATE, and getting this wrong makes the whole
 * feature silently do nothing. Re-validate re-checks a credential; it
 * deliberately does not touch the declaration (`revalidate-connection.ts` omits
 * these fields). What has to change here is what Flexi RECORDS the role may do,
 * which is the declare path. The external id does not change, so there is
 * nothing to reconnect.
 */
export const GrantElevationPrompt = (
    {
        grant,
        currentSelection,
        onGrant,
        onDecline,
        isSaving = false,
        manualAlternative,
    }: {
        readonly grant: RoleGrantKey,
        /**
         * What the connection declares TODAY. The policy offered is this plus
         * the one grant, so the customer keeps everything they already have.
         */
        readonly currentSelection: RoleGrantSelection,
        /** Re-declare with the grant added. */
        readonly onGrant: () => void,
        /** Record the decline and stop asking. */
        readonly onDecline: () => void,
        readonly isSaving?: boolean,
        /**
         * What the customer does INSTEAD, in their own AWS console, in their own
         * terms. Supplied by the caller because it names the specific thing —
         * this hosted zone, this certificate — and a generic sentence would be
         * the vague prose FNP-1363 replaced.
         */
        readonly manualAlternative: string,
    },
) => {

    const definition = grantDefinition(grant);

    // A grant with no definition cannot be described, and inventing copy for it
    // would be the second source of truth this feature exists to avoid.
    if (!definition) {
        return null;
    }

    const policy = elevatedPolicyFor(currentSelection, grant);
    const actions = actionsAdded(grant);

    return (
        <Alert
            color={'blue'}
            variant={'light'}
            title={I18n.get('This needs one more permission')}
            data-testid={`elevation-prompt-${grant}`}
        >
            <Stack gap={'md'}>

                {/* ⚠️ NAMES WHAT THEY CHOSE, WITHOUT REPROACH. They withheld
                    this deliberately and were told what it would cost; meeting
                    that cost is the system working. The sentence is the same
                    `withholdingCost` the connect drawer showed, because it is
                    the same fact — now arriving at the moment it bites. */}
                <Stack gap={'xs'}>
                    <Group gap={'xs'}>
                        <Text size={'sm'} fw={500}>
                            {I18n.get(definition.label)}
                        </Text>
                        <Badge size={'xs'} color={'orange'} variant={'light'}>
                            {I18n.get('destructive')}
                        </Badge>
                    </Group>
                    <Text size={'sm'}>
                        {productCopy('You chose not to grant this when you connected, which is why {product} cannot do it for you.')}
                    </Text>
                    {definition.withholdingCost && (
                        <Text size={'xs'} c={'dimmed'} fs={'italic'}>
                            {I18n.get(definition.withholdingCost)}
                        </Text>
                    )}
                </Stack>

                {/* The literal actions, named. The person careful enough to
                    have withheld this is precisely the person who needs to see
                    exactly what is being added rather than a description. */}
                <Stack gap={4}>
                    <Text size={'sm'}>
                        {actions.length === 1
                            ? I18n.get('Granting it adds one action to your role:')
                            : I18n.get('Granting it adds these actions to your role:')}
                    </Text>
                    <List size={'xs'}>
                        {actions.map(action => (
                            <List.Item key={action}>
                                <Code>{action}</Code>
                            </List.Item>
                        ))}
                    </List>
                </Stack>

                {/* ⚠️ THE COMPLETE POLICY, AND THE WORD "REPLACE" IS LOAD-BEARING.
                    A customer who pastes this as an addition alongside their
                    existing statements is fine; one who is led to believe this
                    is a fragment and hand-merges it is where mistakes happen.
                    Saying "replaces" makes the safe action the obvious one. */}
                <Stack gap={'xs'}>
                    <Group justify={'space-between'}>
                        <Text size={'sm'} fw={500}>
                            {I18n.get('Your updated permission policy')}
                        </Text>
                        <FlexiCopyButton
                            value={policy}
                            data-testid={'elevation-policy-copy'}
                        />
                    </Group>
                    <Text size={'xs'} c={'dimmed'}>
                        {I18n.get('This is your whole policy with the new action included — it replaces the one on your role, so nothing you already granted is lost.')}
                    </Text>
                    <Code block data-testid={'elevation-policy'}>
                        {policy}
                    </Code>
                    <Anchor
                        size={'xs'}
                        href={CONNECT_PROVIDER_DOCS_URL}
                        target={'_blank'}
                        rel={'noopener noreferrer'}
                    >
                        {I18n.get('How to update the policy on your role')}
                    </Anchor>
                </Stack>

                {/* ⚠️ NO RECONNECTION, SAID OUT LOUD. The external id does not
                    change when a role is widened, so a customer bracing for the
                    whole connect flow again needs telling that this is one
                    button. */}
                <Text size={'xs'} c={'dimmed'}>
                    {I18n.get('Update the policy in AWS first, then confirm below. Your connection and its external ID stay as they are — there is nothing to reconnect.')}
                </Text>

                <Group justify={'space-between'} align={'flex-start'}>
                    {/* ⚠️ DECLINING IS A FIRST-CLASS CHOICE AND SAYS WHAT
                        HAPPENS NEXT. "No thanks" that leaves someone stuck is
                        the wall the ticket forbids; this one hands them the
                        manual path in the same breath. */}
                    <Stack gap={4} style={{flex: 1}}>
                        <Button
                            variant={'subtle'}
                            size={'compact-sm'}
                            onClick={onDecline}
                            disabled={isSaving}
                            data-testid={'elevation-decline'}
                        >
                            {I18n.get('No thanks, I will do it myself')}
                        </Button>
                        <Text size={'xs'} c={'dimmed'}>
                            {I18n.get(manualAlternative)}
                        </Text>
                        <Text size={'xs'} c={'dimmed'}>
                            {productCopy('{product} will not ask again for this connection. You can still grant it later from the connection page.')}
                        </Text>
                    </Stack>

                    <Button
                        size={'sm'}
                        onClick={onGrant}
                        loading={isSaving}
                        data-testid={'elevation-grant'}
                    >
                        {I18n.get('I have added it')}
                    </Button>
                </Group>

            </Stack>
        </Alert>
    );
};
